import { formatError } from '@signal24/vue-foundation';
import { type Dictionary, keyBy } from 'lodash';
import { type Ref, computed, ref } from 'vue';

import { AccountsApi, MspApi, RolesApi } from '@/openapi-clients-generated/auth';
import {
    CoreLocationsApi,
    FormsApi,
    InventoryCategoriesApi,
    InventoryVendorsApi,
    SalesConfigCustomPaymentMethodsApi,
    SalesConfigProductCategoriesApi,
    SalesConfigTaxesApi,
    SalesFinancialAccountsApi,
    SalesFulfillmentBoxTypesApi
} from '@/openapi-clients-generated/suite';
import {
    AccessApi,
    EndpointConfigsApi,
    ExtensionsApi,
    FpbxRegistrationsApi,
    NumbersApi,
    SubscribedNumbersApi,
    VoicemailApi
} from '@/openapi-clients-generated/talk';
import { type OpenApiDataType, dataFrom } from '@signal24/openapi-client-codegen';

const CacheLoaders = {
    authRoles: () => RolesApi.getRolesList(),
    authAccounts: () => AccountsApi.getAccountsList(),
    coreLocations: () => CoreLocationsApi.getCoreLocationsIndex(),
    forms: () => FormsApi.getFormsListForms({}),
    inventoryCategories: () => InventoryCategoriesApi.getInventoryCategoriesIndex(),
    inventoryVendors: () => InventoryVendorsApi.getInventoryVendorsIndex(),
    salesCategories: () => SalesConfigProductCategoriesApi.getSalesConfigProductCategoriesIndex(),
    salesCustomPaymentMethods: () => SalesConfigCustomPaymentMethodsApi.getSalesConfigCustomPaymentMethodsIndex(),
    salesFulfillmentBoxTypes: () => SalesFulfillmentBoxTypesApi.getSalesFulfillmentBoxTypesList(),
    salesTaxes: () => SalesConfigTaxesApi.getSalesConfigTaxesIndex(),
    salesFinancialAccounts: () => SalesFinancialAccountsApi.getSalesFinancialAccountsIndex(),
    talkAccess: () => AccessApi.getAccessGetComposite(),
    talkNumbers: () => NumbersApi.getNumbersList(),
    talkExtensions: () => ExtensionsApi.getExtensionsListExtensions(),
    talkEndpointConfigurations: () => EndpointConfigsApi.getEndpointConfigsListConfigurations(),
    talkFpbxRegistrations: () => FpbxRegistrationsApi.getFpbxRegistrationsGetRegistrations(),
    talkSubscribedNumbers: () => SubscribedNumbersApi.getSubscribedNumbersIndex(),
    talkVoicemailCounts: () => VoicemailApi.getVoicemailGetCounts(),
    mspTenants: () => MspApi.getMspGetMspTenants()
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export type ICacheKey = keyof typeof CacheLoaders;

export type CacheDataType<T extends ICacheKey> = OpenApiDataType<Awaited<ReturnType<(typeof CacheLoaders)[T]>>>;

type TypeWithId = {
    // eslint-disable-next-line no-explicit-any
    [K in ICacheKey]: CacheDataType<K> extends any[] ? (CacheDataType<K>[number] extends { id: any } ? K : never) : never;
}[ICacheKey];

export interface AsyncRef<T> extends Ref<T> {
    load?: () => Promise<void>;
    reload?: () => Promise<void>;
    backgroundReload?: () => Promise<void>;
    loadError?: string;
    loadPromise?: Promise<void>;
}
export function asyncRef<T>(value: T): AsyncRef<T>;
export function asyncRef<T>(): AsyncRef<T | undefined>;
export function asyncRef<T>(value?: T): AsyncRef<T | undefined> {
    return ref(value) as AsyncRef<T>;
}

export class DataCache {
    static refCache: { [K in ICacheKey]?: AsyncRef<CacheDataType<K> | undefined> } = {};

    static waitFor<T extends ICacheKey>(key: T): Promise<void> {
        if (!this.refCache[key]) {
            return this.getRef(key).loadPromise!;
        }
        if (this.refCache[key]?.loadPromise) {
            return this.refCache[key]!.loadPromise;
        }
        return Promise.resolve();
    }

    static getLazyRef<T extends ICacheKey>(key: T): AsyncRef<CacheDataType<T> | undefined> {
        if (!this.refCache[key]) {
            const newRef = ref<CacheDataType<T> | undefined>() as AsyncRef<CacheDataType<T> | undefined>;
            const load = () => {
                return CacheLoaders[key]().then(
                    result => {
                        // eslint-disable-next-line no-explicit-any
                        newRef.value = dataFrom(result as any) as CacheDataType<T>;
                    },
                    err => {
                        newRef.loadError = formatError(err);
                    }
                );
            };
            newRef.load = () => {
                newRef.loadPromise ??= load();
                return newRef.loadPromise;
            };
            newRef.reload = () => {
                newRef.value = undefined;
                newRef.loadError = undefined;
                return (newRef.loadPromise = load());
            };
            newRef.backgroundReload = () => {
                newRef.loadError = undefined;
                return (newRef.loadPromise = load());
            };
            // eslint-disable-next-line no-explicit-any
            this.refCache[key] = newRef as any; // ts issue
        }
        return this.refCache[key]!;
    }

    static getRef<T extends ICacheKey>(key: T): AsyncRef<CacheDataType<T> | undefined> {
        const ref = this.getLazyRef(key);
        if (!ref.loadPromise) ref.reload?.();
        return ref;
    }

    static getMapById<T extends TypeWithId>(key: T) {
        const ref = this.getRef(key);
        return computed(() => keyBy(ref.value, 'id') as Dictionary<CacheDataType<T>[number]>);
    }

    static reloadCaches() {
        for (const [_key, ref] of Object.entries(this.refCache)) {
            ref.reload?.();
        }
    }

    static reloadCache<T extends ICacheKey>(key: T, background = false) {
        if (this.refCache[key]) {
            this.refCache[key][background ? 'backgroundReload' : 'reload']?.();
        }
    }
}
